import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { ReportingKPI } from '../../../utils/types/reporting/reporting_types';

export interface KPICardProps {
  kpi: {
    label: string;
    value: number | string;
    unit?: string;
    trend?: number | null;
    trendDirection?: 'up' | 'down' | null;
  };
  loading?: boolean;
  icon?: ReactNode;
}

export const KPICard: React.FC<KPICardProps> = ({ kpi, loading = false, icon }) => {
  if (loading) {
    return (
      <div className='rounded-xl border border-gray-100 bg-white p-5 shadow-sm'>
        <div className='flex items-start gap-3 animate-pulse'>
          <div className='h-10 w-10 flex-shrink-0 rounded-lg bg-gray-100' />
          <div className='flex-1 space-y-2'>
            <div className='h-7 w-16 rounded bg-gray-200' />
            <div className='h-3 w-24 rounded bg-gray-100' />
          </div>
        </div>
        <div className='mt-3 h-5 w-14 animate-pulse rounded-full bg-gray-100' />
      </div>
    );
  }

  const trendVal = kpi.trend;
  const hasTrend = trendVal !== undefined && trendVal !== null && kpi.trendDirection;
  const isUp = kpi.trendDirection === 'up';
  const trendClasses = isUp
    ? 'bg-emerald-50 text-emerald-700'
    : 'bg-red-50 text-red-600';

  return (
    <div className='rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow duration-150 ease-in-out hover:shadow-md'>
      <div className='flex items-start gap-3'>
        { icon && (
          <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600'>
            { icon }
          </div>
        ) }
        <div className='min-w-0'>
          <p className='text-3xl font-bold text-gray-900'>
            { kpi.value }
            { kpi.unit && (
              <span className='ml-1 text-base font-medium text-gray-400'>{ kpi.unit }</span>
            ) }
          </p>
          <p className='mt-1 text-sm text-gray-500'>{ kpi.label }</p>
        </div>
      </div>
      { hasTrend && (
        <div className='mt-3'>
          <span
              className={classNames(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                trendClasses,
              )}
          >
            <span>{ isUp ? '↑' : '↓' }</span>
            <span>{ Math.abs(trendVal) }%</span>
          </span>
        </div>
      ) }
    </div>
  );
};
